import React, { useState, useEffect } from "react";
import { X, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DEMO_EMAIL = "demo@biz360.fr";
const POPUP_DELAY_MS = 120000; // 2 minutes
const POPUP_STORAGE_KEY = "biz360_demo_popup_shown";

export const DemoConversionPopup = () => {
  const { user } = useAuth();
  const [showPopup, setShowPopup] = useState(false);

  const isDemo = user?.email?.toLowerCase() === DEMO_EMAIL.toLowerCase();

  useEffect(() => {
    if (!isDemo) return;

    // Vérifier si le popup a déjà été montré cette session
    const alreadyShown = sessionStorage.getItem(POPUP_STORAGE_KEY);
    if (alreadyShown) return;

    // Afficher le popup après 2 minutes
    const timer = setTimeout(() => {
      setShowPopup(true);
      sessionStorage.setItem(POPUP_STORAGE_KEY, "true");
    }, POPUP_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isDemo]);

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white text-center">
          <Sparkles className="mx-auto mb-3" size={40} />
          <h2 className="text-2xl font-bold">Tu aimes ce que tu vois ?</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-center mb-6">
            Tu explores Biz360 avec des données fictives.<br />
            <strong className="text-gray-800">Imagine la même chose avec TES vrais chiffres !</strong>
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <span className="text-2xl">📊</span>
              <span className="text-sm">Ton CA calculé automatiquement</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <span className="text-2xl">🧾</span>
              <span className="text-sm">Tes cotisations Urssaf exactes</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <span className="text-2xl">⏰</span>
              <span className="text-sm">Alertes personnalisées TVA/seuils</span>
            </div>
          </div>

          <a
            href="/subscription"
            className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
          >
            Créer mon compte <ArrowRight size={18} />
          </a>

          <button
            onClick={() => setShowPopup(false)}
            className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Continuer la démo
          </button>
        </div>

        {/* Close button */}
        <button
          onClick={() => setShowPopup(false)}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
        >
          <X size={24} />
        </button>
      </div>
    </div>
  );
};

// Messages contextuels à afficher dans l'interface démo
export const DemoContextMessage = ({ type = "default" }) => {
  const { user } = useAuth();
  const isDemo = user?.email?.toLowerCase() === DEMO_EMAIL.toLowerCase();

  if (!isDemo) return null;

  const messages = {
    dashboard: {
      icon: "✨",
      text: "Imagine TES chiffres ici...",
      cta: "Crée ton compte"
    },
    ventes: {
      icon: "💰",
      text: "Tes vraies ventes, ton vrai CA",
      cta: "C'est parti !"
    },
    depenses: {
      icon: "📝",
      text: "Suis TES dépenses facilement",
      cta: "Je me lance"
    },
    declaration: {
      icon: "🧾",
      text: "Plus jamais de prise de tête avec l'Urssaf",
      cta: "J'en profite"
    },
    default: {
      icon: "✨",
      text: "Prêt(e) à piloter ta vraie activité ?",
      cta: "Créer mon compte"
    }
  };

  const msg = messages[type] || messages.default;

  return (
    <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border border-purple-200 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{msg.icon}</span>
        <span className="font-medium text-purple-800">{msg.text}</span>
      </div>
      <a
        href="/subscription"
        className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap"
      >
        {msg.cta}
      </a>
    </div>
  );
};
